/// <reference lib="dom" />
/**
 * Tiles drawn in the editor's pixel editor. Each tileset (terrain style) has one custom sheet, stored
 * as the asset 'custom-tiles/<style id>' and installed at runtime as an extra texture, so custom
 * tiles are ordinary sprites everywhere (tile cases, painting, play mode). The imported Unity art is
 * never changed.
 *
 * Sprite names: `<style>-custom_<n>`, colour variants `<style>-custom_<n>@<variant>`.
 */
import type { PaletteEntry } from '../model/format.ts';
import type { SpriteDef, TextureDef } from '../model/gamedata.ts';
import { gameData, readyImage, registerTexture, texturesChanged } from './gameData.ts';
import { loadAsset, saveAsset } from './remoteStore.ts';
import { decodePx, encodePx, type Img, blank } from '../editor/pixel/img.ts';
import { EMPTY, shapePoly } from '../terrain/shapes.ts';

export const TILE_PX = 16;
const COLS = 16;
const HISTORY_MAX = 20;

export interface CustomTileVersion {
  at: string;
  px: string;
  variants?: Record<string, string>;
}

export interface CustomTile {
  /** sprite number, never reused */
  n: number;
  /** canonical colours: base64 RGBA, rows from the top */
  px: string;
  /** colour variant name -> pixels */
  variants?: Record<string, string>;
  /** collision: this terrain cell shape (SHAPES id); absent: the drawn pixels' alpha */
  shape?: number;
  /** what it was drawn for, e.g. "grass case gq..." */
  note?: string;
  made: string;
  updated: string;
  /** earlier saved versions, oldest first */
  history?: CustomTileVersion[];
}

export interface CustomSheet {
  /** texture guid (32 hex, also used for a future Unity import) */
  guid: string;
  next: number;
  tiles: CustomTile[];
  /** colours saved with the tileset's palette */
  colors?: string[];
  /** hand-edited palette swaps: "<from variant>><to variant>" -> from hex -> to hex ('' = canonical) */
  swaps?: Record<string, Record<string, string>>;
}

const sheets = new Map<string, CustomSheet>();
const texToStyle = new Map<string, string>();
const canvases = new Map<string, HTMLCanvasElement>();
/** sprite name -> slot index on the sheet canvas */
const slots = new Map<string, Map<string, number>>();
const listeners = new Set<() => void>();

function structureChanged() {
  for (const l of listeners) l();
}

/** Custom tiles added, removed or given a new colour version (terrain style tables change). */
export function onCustomTilesChanged(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export const customName = (styleId: string, n: number, variant?: string | null) => `${styleId}-custom_${n}${variant ? `@${variant}` : ''}`;

export function parseCustomName(name: string): { n: number; variant: string | null } | null {
  const m = /-custom_(\d+)(?:@(.+))?$/.exec(name);
  return m ? { n: +m[1], variant: m[2] ?? null } : null;
}

export function customSheet(styleId: string): CustomSheet | null {
  return sheets.get(styleId) ?? null;
}

export function isCustomTex(tex: string): boolean {
  return texToStyle.has(tex);
}

/** The custom tile a palette entry shows (null for imported art). */
export function customTileOf(pe: PaletteEntry | null | undefined): { styleId: string; tile: CustomTile; variant: string | null } | null {
  if (!pe) return null;
  const styleId = texToStyle.get(pe.tex);
  const p = styleId ? parseCustomName(pe.sprite) : null;
  const tile = p && sheets.get(styleId!)?.tiles.find((t) => t.n === p.n);
  return tile ? { styleId: styleId!, tile, variant: p!.variant } : null;
}

export function customEntry(styleId: string, n: number, variant?: string | null): PaletteEntry | null {
  const s = sheets.get(styleId);
  return s ? { tex: s.guid, sprite: customName(styleId, n, variant) } : null;
}

/** All canonical custom tiles of a style as palette entries. */
export function customEntries(styleId: string): PaletteEntry[] {
  const s = sheets.get(styleId);
  return s ? s.tiles.map((t) => ({ tex: s.guid, sprite: customName(styleId, t.n) })) : [];
}

/** The colour variant version of a custom tile entry (null: it has none, the canonical one is used). */
export function customVariantEntry(pe: PaletteEntry, variant: string): PaletteEntry | null {
  const c = customTileOf(pe);
  if (!c || !c.tile.variants?.[variant]) return null;
  return { tex: pe.tex, sprite: customName(c.styleId, c.tile.n, variant) };
}

export function customCanonicalEntry(pe: PaletteEntry): PaletteEntry {
  const c = customTileOf(pe);
  return c && c.variant ? { tex: pe.tex, sprite: customName(c.styleId, c.tile.n) } : pe;
}

export function tileImg(t: CustomTile, variant?: string | null): Img {
  return decodePx((variant && t.variants?.[variant]) || t.px, TILE_PX);
}

function newGuid(): string {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return [...a].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function physics(t: CustomTile): SpriteDef['s'] {
  if (t.shape === undefined || t.shape === EMPTY) return undefined;
  const h = TILE_PX / 2;
  return [shapePoly(t.shape).map(([x, y]) => [x * TILE_PX - h, y * TILE_PX - h] as [number, number])];
}

function variantNames(t: CustomTile): string[] {
  return Object.keys(t.variants ?? {}).sort();
}

/** Rebuild the sheet texture (layout and pixels) and install it. */
function install(styleId: string) {
  const s = sheets.get(styleId);
  if (!s) return;
  let gd;
  try {
    gd = gameData();
  } catch {
    return;
  }
  void gd;
  const list: { name: string; img: Img; id: string; shape: SpriteDef['s'] }[] = [];
  for (const t of s.tiles) {
    list.push({ name: customName(styleId, t.n), img: tileImg(t), id: String(2130000000 + t.n * 64), shape: physics(t) });
    variantNames(t).forEach((v, i) => list.push({ name: customName(styleId, t.n, v), img: tileImg(t, v), id: String(2130000000 + t.n * 64 + i + 1), shape: physics(t) }));
  }
  const rows = Math.max(1, Math.ceil(list.length / COLS));
  const W = COLS * TILE_PX, H = rows * TILE_PX;
  const canvas = canvases.get(styleId) ?? document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const g = canvas.getContext('2d')!;
  g.clearRect(0, 0, W, H);
  const sm = new Map<string, number>();
  const sprites: SpriteDef[] = list.map((e, i) => {
    const col = i % COLS, row = Math.floor(i / COLS);
    g.putImageData(new ImageData(new Uint8ClampedArray(e.img.d), TILE_PX, TILE_PX), col * TILE_PX, row * TILE_PX);
    sm.set(e.name, i);
    const def: SpriteDef = { n: e.name, id: e.id, r: [col * TILE_PX, H - (row + 1) * TILE_PX, TILE_PX, TILE_PX], p: [0.5, 0.5] };
    if (e.shape) def.s = e.shape;
    return def;
  });
  canvases.set(styleId, canvas);
  slots.set(styleId, sm);
  texToStyle.set(s.guid, styleId);
  const def: TextureDef = { path: `custom-tiles/${styleId}.png`, w: W, h: H, ppu: TILE_PX, sprites };
  registerTexture(s.guid, def, canvas);
}

function isCustomSheet(v: unknown): v is CustomSheet {
  if (!v || typeof v !== 'object') return false;
  const s = v as CustomSheet;
  return typeof s.guid === 'string' && typeof s.next === 'number' && Array.isArray(s.tiles);
}

/** Install a sheet another editor sent (or that we just fetched). Does not write the server copy. */
export function applyCustomSheet(styleId: string, data: unknown): boolean {
  if (!isCustomSheet(data)) return false;
  const prev = sheets.get(styleId);
  if (prev && prev.guid !== data.guid) texToStyle.delete(prev.guid);
  sheets.set(styleId, data);
  try {
    install(styleId);
  } catch {
    return false;
  }
  structureChanged();
  return true;
}

/** Load the custom sheets of the given styles and install their textures. */
export async function loadCustomTiles(styleIds: string[]): Promise<void> {
  await Promise.all(
    styleIds.map(async (id) => {
      try {
        const s = await loadAsset<CustomSheet>('custom-tiles', id);
        if (s && s.guid && Array.isArray(s.tiles)) {
          sheets.set(id, s);
          install(id);
        }
      } catch {
        /* none */
      }
    }),
  );
  structureChanged();
}

function ensureSheet(styleId: string): CustomSheet {
  let s = sheets.get(styleId);
  if (!s) sheets.set(styleId, (s = { guid: newGuid(), next: 0, tiles: [] }));
  return s;
}

/** Add a tile (not saved until saveCustomSheet). */
export function createCustomTile(styleId: string, img: Img, opts: { shape?: number; note?: string; variants?: Record<string, Img> } = {}): CustomTile {
  const s = ensureSheet(styleId);
  const now = new Date().toISOString();
  const t: CustomTile = { n: s.next++, px: encodePx(img), made: now, updated: now };
  if (opts.shape !== undefined) t.shape = opts.shape;
  if (opts.note) t.note = opts.note;
  if (opts.variants) t.variants = Object.fromEntries(Object.entries(opts.variants).map(([k, v]) => [k, encodePx(v)]));
  s.tiles.push(t);
  install(styleId);
  structureChanged();
  return t;
}

export function removeCustomTile(styleId: string, n: number) {
  const s = sheets.get(styleId);
  if (!s) return;
  s.tiles = s.tiles.filter((t) => t.n !== n);
  install(styleId);
  structureChanged();
}

export function findCustomTile(styleId: string, n: number): CustomTile | null {
  return sheets.get(styleId)?.tiles.find((t) => t.n === n) ?? null;
}

/**
 * Set a tile's pixels (one colour version; variant null = canonical). Redraws the sheet in place, or
 * relays it out when a colour version is added or removed (img null removes a variant).
 */
export function setTilePixels(styleId: string, n: number, variant: string | null, img: Img | null) {
  const t = findCustomTile(styleId, n);
  if (!t) return;
  if (!variant) {
    if (!img) return;
    t.px = encodePx(img);
  } else if (!img) {
    if (t.variants) delete t.variants[variant];
    install(styleId);
    structureChanged();
    return;
  } else {
    const added = !t.variants?.[variant];
    (t.variants ??= {})[variant] = encodePx(img);
    if (added) {
      install(styleId);
      structureChanged();
      return;
    }
  }
  const slot = slots.get(styleId)?.get(customName(styleId, n, variant));
  const canvas = canvases.get(styleId);
  if (slot === undefined || !canvas) return install(styleId);
  canvas.getContext('2d')!.putImageData(new ImageData(new Uint8ClampedArray(img.d), TILE_PX, TILE_PX), (slot % COLS) * TILE_PX, Math.floor(slot / COLS) * TILE_PX);
  texturesChanged();
}

/** Put back a saved version of a tile (pixels and colour versions). */
export function restoreTile(styleId: string, n: number, v: CustomTileVersion) {
  const t = findCustomTile(styleId, n);
  if (!t) return;
  t.px = v.px;
  if (v.variants && Object.keys(v.variants).length) t.variants = { ...v.variants };
  else delete t.variants;
  install(styleId);
  structureChanged();
}

export function setTileShape(styleId: string, n: number, shape: number | undefined) {
  const t = findCustomTile(styleId, n);
  if (!t) return;
  if (shape === undefined) delete t.shape;
  else t.shape = shape;
  install(styleId);
}

/** Keep the tile's previous saved version in its history (call before saving new pixels). */
export function pushHistory(t: CustomTile, prev: CustomTileVersion) {
  if (prev.px === t.px && JSON.stringify(prev.variants ?? {}) === JSON.stringify(t.variants ?? {})) return;
  t.history = [...(t.history ?? []), prev].slice(-HISTORY_MAX);
  t.updated = new Date().toISOString();
}

export function setSheetColors(styleId: string, colors: string[]) {
  ensureSheet(styleId).colors = colors;
}

export function setSheetSwap(styleId: string, key: string, map: Record<string, string> | null) {
  const s = ensureSheet(styleId);
  if (map && Object.keys(map).length) (s.swaps ??= {})[key] = map;
  else if (s.swaps) delete s.swaps[key];
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

/** Store a style's custom sheet (debounced). */
export function saveCustomSheet(styleId: string, delay = 300): Promise<void> {
  const prev = timers.get(styleId);
  if (prev) clearTimeout(prev);
  return new Promise((resolve, reject) => {
    timers.set(
      styleId,
      setTimeout(() => {
        timers.delete(styleId);
        const s = sheets.get(styleId);
        if (!s) return resolve();
        saveAsset('custom-tiles', styleId, s).then(resolve, reject);
      }, delay),
    );
  });
}

// ------------------------------------------------------------------ reading texture pixels

const texPixels = new Map<string, Img>();

/** RGBA of a whole texture (rows from the top); null until the image is loaded. */
export function texturePixels(tex: string): Img | null {
  const styleId = texToStyle.get(tex);
  const img = readyImage(styleId ? canvases.get(styleId) : gameData().images.get(tex));
  if (!img) return null;
  if (!styleId) {
    const hit = texPixels.get(tex);
    if (hit) return hit;
  }
  const c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  const g = c.getContext('2d', { willReadFrequently: true })!;
  g.drawImage(img, 0, 0);
  const out: Img = { w: c.width, h: c.height, d: g.getImageData(0, 0, c.width, c.height).data };
  if (!styleId) texPixels.set(tex, out);
  return out;
}

/** A sprite's pixels as a TILE_PX square (centred; bigger sprites are cropped). */
export function spritePixels(pe: PaletteEntry | null | undefined): Img | null {
  if (!pe) return null;
  const c = customTileOf(pe);
  if (c) return tileImg(c.tile, c.variant);
  let gd;
  try {
    gd = gameData();
  } catch {
    return null;
  }
  const ref = gd.spriteByName.get(`${pe.tex}|${pe.sprite}`);
  const tp = ref && texturePixels(pe.tex);
  if (!ref || !tp) return null;
  const [rx, ry, rw, rh] = ref.def.r;
  const top = ref.texDef.h - ry - rh;
  const o = blank(TILE_PX);
  const ox = Math.floor((rw - TILE_PX) / 2), oy = Math.floor((rh - TILE_PX) / 2);
  for (let y = 0; y < TILE_PX; y++)
    for (let x = 0; x < TILE_PX; x++) {
      const sx = rx + ox + x, sy = top + oy + y;
      if (sx < rx || sy < top || sx >= rx + rw || sy >= top + rh) continue;
      const i = (sy * tp.w + sx) * 4, j = (y * TILE_PX + x) * 4;
      o.d[j] = tp.d[i];
      o.d[j + 1] = tp.d[i + 1];
      o.d[j + 2] = tp.d[i + 2];
      o.d[j + 3] = tp.d[i + 3];
    }
  return o;
}
